import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { pageStateQueryOptions, PageAccessError } from "@/lib/queries";
import { PageError } from "@/features/auth/components/PageError";
import { usePageState } from "@/hooks/usePageState";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { MarketplacePage } from "@/features/marketplace/pages/MarketplacePage";

const marketplaceSearchSchema = z.object({
  tab: z.enum(["catalog", "connections", "orders"]).optional().catch(undefined),
  /** Order id to open + highlight when landing on the orders tab (deep link). */
  order: z.string().optional().catch(undefined),
});

export type MarketplaceSearch = z.infer<typeof marketplaceSearchSchema>;
export type MarketplaceTab = NonNullable<MarketplaceSearch["tab"]>;

function MarketplaceRoute() {
  const { t } = useTranslation();
  useDocumentTitle(t("marketplace.title", "Marketplace"));

  const { tab, order } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { isLoading, error } = usePageState("marketplace");

  if (isLoading) return null;
  if (error) return <PageError is403={error instanceof PageAccessError} />;

  return (
    <MarketplacePage
      tab={tab ?? "catalog"}
      highlightOrderId={order}
      onTabChange={(next) =>
        navigate({
          search: (prev) => ({
            ...prev,
            tab: next,
            // Drop the deep-link order id once the user leaves the orders tab.
            order: next === "orders" ? prev.order : undefined,
          }),
          replace: true,
        })
      }
    />
  );
}

export const Route = createFileRoute("/_authenticated/marketplace/")({
  beforeLoad: () => assertPagePermission("marketplace"),
  validateSearch: (search): MarketplaceSearch => {
    const parsed = marketplaceSearchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  loader: ({ context }) =>
    context.queryClient
      .ensureQueryData(pageStateQueryOptions("marketplace"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      }),
  component: MarketplaceRoute,
  errorComponent: ({ error }) => (
    <PageError is403={error instanceof PageAccessError} />
  ),
});
